// Phase B: deterministic FlowPatch engine.
//
// Instead of regenerating and full-replacing the flow, the AI (or a local
// fallback) returns a FlowPatch describing only the delta. This engine
// validates it, resolves natural-language anchors against the current
// canonical draft, and applies the operations atomically: on ANY error the
// original draft is returned unchanged (worst case == no change), and every
// failure is a structured error (never a silent drop).
//
// FlowPatch:
//   { flowPatchVersion:"1.0", baseSignature?, summary?, operations:[ ... ] }
// Operations:
//   { op:"addMotion", axisName, motion:{name,distance?,accel?,decel?,maxSpeed?,waitTime?} }
//   { op:"addNode", node:{ id?, type, ... , x?, y? } }
//   { op:"connect", from, to, output?, input? }
//   { op:"disconnect", from, to?, output? }
//   { op:"insertNodeAfter", anchorRef, newNodeId, anchorOutput? }
//   { op:"removeNode", nodeId }
//   { op:"setNodeData", nodeId, patch:{...} }

import {
    upsertDraftNode,
    removeDraftNodeById,
    removeDraftConnections,
    upsertDraftConnection,
    findDraftAxis
} from "./draft.js";

const KNOWN_OPS = new Set([
    "addMotion",
    "addNode",
    "connect",
    "disconnect",
    "insertNodeAfter",
    "removeNode",
    "setNodeData"
]);

function cloneDraft(draft) {
    return JSON.parse(JSON.stringify(draft || {}));
}

function err(code, message, opIndex) {
    return { code, message, ...(Number.isInteger(opIndex) ? { opIndex } : {}) };
}

function nodeById(draft, id) {
    return (draft.nodes || []).find((n) => String(n.id) === String(id)) || null;
}

function maxNodeX(draft) {
    return (draft.nodes || []).reduce((m, n) => Math.max(m, Number(n.x) || 0), 0);
}

export function validateFlowPatch(patch) {
    const errors = [];
    if (!patch || typeof patch !== "object") {
        return { ok: false, errors: [err("PATCH_INVALID", "patch is not an object")] };
    }
    if (patch.flowPatchVersion && patch.flowPatchVersion !== "1.0") {
        errors.push(err("PATCH_VERSION", `unsupported flowPatchVersion: ${patch.flowPatchVersion}`));
    }
    if (!Array.isArray(patch.operations)) {
        errors.push(err("PATCH_INVALID", "patch.operations must be an array"));
        return { ok: false, errors };
    }
    patch.operations.forEach((op, i) => {
        if (!op || typeof op !== "object" || !op.op) {
            errors.push(err("OP_INVALID", "operation missing 'op'", i));
        } else if (!KNOWN_OPS.has(op.op)) {
            errors.push(err("OP_UNKNOWN", `unknown op: ${op.op}`, i));
        }
    });
    return { ok: errors.length === 0, errors };
}

// anchorRef: string id | { id } | { nodeId } | { motionName, axisName? }
//          | { nodeType, label? , conditionText? }
export function resolveAnchor(draft, anchorRef) {
    const nodes = draft.nodes || [];
    if (typeof anchorRef === "string") {
        const n = nodeById(draft, anchorRef);
        return n ? { ok: true, nodeId: String(n.id) } : { ok: false, ...err("ANCHOR_NOT_FOUND", `node id not found: ${anchorRef}`) };
    }
    if (!anchorRef || typeof anchorRef !== "object") {
        return { ok: false, ...err("ANCHOR_INVALID", "anchorRef is invalid") };
    }
    const explicitId = anchorRef.id ?? anchorRef.nodeId;
    if (explicitId != null) {
        const n = nodeById(draft, explicitId);
        return n ? { ok: true, nodeId: String(n.id) } : { ok: false, ...err("ANCHOR_NOT_FOUND", `node id not found: ${explicitId}`) };
    }
    if (anchorRef.motionName) {
        const matches = nodes.filter(
            (n) =>
                n.type === "motion" &&
                String(n.motionName || "") === String(anchorRef.motionName) &&
                (!anchorRef.axisName || String(n.axisName || "") === String(anchorRef.axisName))
        );
        if (matches.length === 1) return { ok: true, nodeId: String(matches[0].id) };
        if (matches.length === 0) {
            return { ok: false, ...err("ANCHOR_NOT_FOUND", `motion node not found: ${anchorRef.axisName || ""} ${anchorRef.motionName}`.trim()) };
        }
        return { ok: false, ...err("ANCHOR_AMBIGUOUS", `multiple motion nodes match: ${anchorRef.motionName}`) };
    }
    if (anchorRef.nodeType) {
        const label = anchorRef.label ?? anchorRef.conditionText;
        const matches = nodes.filter(
            (n) =>
                n.type === anchorRef.nodeType &&
                (label == null ||
                    String(n.label || n.conditionText || n.errorCode || "") === String(label))
        );
        if (matches.length === 1) return { ok: true, nodeId: String(matches[0].id) };
        if (matches.length === 0) {
            return { ok: false, ...err("ANCHOR_NOT_FOUND", `node not found: ${anchorRef.nodeType} ${label || ""}`.trim()) };
        }
        return { ok: false, ...err("ANCHOR_AMBIGUOUS", `multiple nodes match: ${anchorRef.nodeType}`) };
    }
    return { ok: false, ...err("ANCHOR_INVALID", "anchorRef has no usable selector") };
}

function opAddMotion(draft, op, ctx) {
    const axisName = String(op.axisName || "").trim();
    const motion = op.motion && typeof op.motion === "object" ? op.motion : null;
    if (!axisName) return err("AXIS_NAME_MISSING", "addMotion requires axisName", ctx.index);
    if (!motion || !String(motion.name || "").trim()) {
        return err("MOTION_NAME_MISSING", "addMotion requires motion.name", ctx.index);
    }
    let axis = (draft.axes || []).find((a) => String(a.name || "") === axisName);
    if (!axis) {
        axis = { name: axisName, motions: [] };
        draft.axes = Array.isArray(draft.axes) ? draft.axes : [];
        draft.axes.push(axis);
    }
    axis.motions = Array.isArray(axis.motions) ? axis.motions : [];
    const id = String(motion.id || ctx.genId("m"));
    if (axis.motions.some((m) => String(m.id) === id)) {
        return err("MOTION_ID_DUPLICATE", `motion id already exists: ${id}`, ctx.index);
    }
    axis.motions.push({
        id,
        name: String(motion.name),
        distance: Number.isFinite(Number(motion.distance)) ? Number(motion.distance) : 100,
        accel: Number.isFinite(Number(motion.accel)) ? Number(motion.accel) : 1,
        decel: Number.isFinite(Number(motion.decel)) ? Number(motion.decel) : 1,
        maxSpeed: Number.isFinite(Number(motion.maxSpeed)) ? Number(motion.maxSpeed) : 500,
        waitTime: Number.isFinite(Number(motion.waitTime)) ? Number(motion.waitTime) : 0
    });
    ctx.lastMotion = { axisName, motionId: id, motionName: String(motion.name) };
    return null;
}

function opAddNode(draft, op, ctx) {
    const node = op.node && typeof op.node === "object" ? { ...op.node } : null;
    if (!node || !node.type) return err("NODE_TYPE_MISSING", "addNode requires node.type", ctx.index);
    node.id = String(node.id || ctx.genId("node"));
    if (nodeById(draft, node.id)) return err("NODE_ID_DUPLICATE", `node id already exists: ${node.id}`, ctx.index);
    if (!Number.isFinite(Number(node.x))) node.x = maxNodeX(draft) + 220;
    if (!Number.isFinite(Number(node.y))) node.y = 90;
    if (node.type === "motion") {
        const axisName = String(node.axisName || "");
        const byId = node.motionId
            ? (draft.axes || []).flatMap((a) => (a.motions || []).map((m) => ({ a, m }))).find((x) => String(x.m.id) === String(node.motionId))
            : null;
        const byName = node.motionName
            ? (draft.axes || [])
                  .flatMap((a) => (a.motions || []).map((m) => ({ a, m })))
                  .find((x) => String(x.m.name) === String(node.motionName) && (!axisName || String(x.a.name) === axisName))
            : null;
        const found = byId || byName;
        if (!found) {
            return err(
                "MOTION_UNRESOLVED",
                `motion node references a motion not present in axes: ${axisName} ${node.motionName || node.motionId || ""}`.trim(),
                ctx.index
            );
        }
        node.axisName = String(found.a.name);
        node.motionId = String(found.m.id);
        node.motionName = String(found.m.name);
    }
    upsertDraftNode(draft, node);
    return null;
}

function opConnect(draft, op, ctx) {
    if (!nodeById(draft, op.from)) return err("NODE_NOT_FOUND", `connect.from not found: ${op.from}`, ctx.index);
    if (!nodeById(draft, op.to)) return err("NODE_NOT_FOUND", `connect.to not found: ${op.to}`, ctx.index);
    upsertDraftConnection(draft, {
        from: String(op.from),
        to: String(op.to),
        output: op.output || "output_1",
        input: op.input || "input_1"
    });
    return null;
}

function opDisconnect(draft, op, ctx) {
    if (!nodeById(draft, op.from)) return err("NODE_NOT_FOUND", `disconnect.from not found: ${op.from}`, ctx.index);
    removeDraftConnections(draft, (c) => {
        if (String(c.from) !== String(op.from)) return false;
        if (op.to != null && String(c.to) !== String(op.to)) return false;
        if (op.output != null && (c.output || "output_1") !== op.output) return false;
        return true;
    });
    return null;
}

function opInsertNodeAfter(draft, op, ctx) {
    const resolved = resolveAnchor(draft, op.anchorRef);
    if (!resolved.ok) return { ...resolved, opIndex: ctx.index };
    const anchorId = resolved.nodeId;
    const newNode = nodeById(draft, op.newNodeId);
    if (!newNode) return err("NODE_NOT_FOUND", `insertNodeAfter.newNodeId not found: ${op.newNodeId}`, ctx.index);
    const output = op.anchorOutput || "output_1";
    const successors = (draft.connections || []).filter(
        (c) => String(c.from) === String(anchorId) && (c.output || "output_1") === output
    );
    // splice newNode between anchor and each current successor
    removeDraftConnections(draft, (c) => String(c.from) === String(anchorId) && (c.output || "output_1") === output);
    for (const s of successors) {
        upsertDraftConnection(draft, {
            from: String(newNode.id),
            to: String(s.to),
            output: "output_1",
            input: s.input || "input_1"
        });
    }
    upsertDraftConnection(draft, {
        from: String(anchorId),
        to: String(newNode.id),
        output,
        input: "input_1"
    });
    return null;
}

function opRemoveNode(draft, op, ctx) {
    if (!nodeById(draft, op.nodeId)) return err("NODE_NOT_FOUND", `removeNode.nodeId not found: ${op.nodeId}`, ctx.index);
    removeDraftNodeById(draft, String(op.nodeId));
    return null;
}

function opSetNodeData(draft, op, ctx) {
    const node = nodeById(draft, op.nodeId);
    if (!node) return err("NODE_NOT_FOUND", `setNodeData.nodeId not found: ${op.nodeId}`, ctx.index);
    if (!op.patch || typeof op.patch !== "object") return err("PATCH_INVALID", "setNodeData requires patch object", ctx.index);
    upsertDraftNode(draft, { ...node, ...op.patch, id: node.id });
    return null;
}

const OP_HANDLERS = {
    addMotion: opAddMotion,
    addNode: opAddNode,
    connect: opConnect,
    disconnect: opDisconnect,
    insertNodeAfter: opInsertNodeAfter,
    removeNode: opRemoveNode,
    setNodeData: opSetNodeData
};

export function applyFlowPatchToDraft(sourceDraft, patch, helpers = {}) {
    const structural = validateFlowPatch(patch);
    if (!structural.ok) {
        return { ok: false, draft: sourceDraft, errors: structural.errors, applied: [] };
    }
    let seq = 0;
    const genId = helpers.genId || ((prefix) => `${prefix}-${++seq}`);
    const draft = cloneDraft(sourceDraft);
    const errors = [];
    const applied = [];
    const ctx = { genId, lastMotion: null };

    patch.operations.forEach((op, index) => {
        if (errors.length > 0) return; // atomic: stop at first failure
        ctx.index = index;
        const handler = OP_HANDLERS[op.op];
        const result = handler(draft, op, ctx);
        if (result) errors.push(result);
        else applied.push(op.op);
    });

    if (errors.length > 0) {
        return { ok: false, draft: sourceDraft, errors, applied: [] };
    }
    if (patch.summary) draft.summary = String(patch.summary);
    return { ok: true, draft, errors: [], applied, summary: draft.summary };
}
